import UploadIcon from '@diligentcorp/atlas-react-icons/dist/esm/lens/Upload.js';
import { LensTheme } from '@diligentcorp/atlas-theme-mui-lens';
import { Box, Typography, useTheme } from '@mui/material';
import { useRef } from 'react';
import { Spinner } from 'src/components/atoms';
import { ALL_MIME_TYPES } from 'src/types';

export interface UploadAreaProps {
  isUploading: boolean;
  onFilesSelected: (files: File[]) => void | Promise<void>;
  acceptedMimeTypes?: string[];
  maxFileSizeLabel?: string;
  inputId?: string;
  supportedFormats?: string[];
  variant?: 'default' | 'compact';
}

export const UploadArea = ({
  isUploading,
  onFilesSelected,
  acceptedMimeTypes = ALL_MIME_TYPES,
  maxFileSizeLabel = 'Max. file size: 100 MB',
  inputId = 'case-documents-upload-input',
  supportedFormats,
  variant = 'default',
}: UploadAreaProps) => {
  const { tokens } = useTheme<LensTheme>();
  const color = tokens.semantic.color;
  const sp = tokens.semantic.spacing.desktop.gap;

  const fileInputRef = useRef<HTMLInputElement>(null);

  const openFilePicker = () => {
    fileInputRef.current?.click();
  };

  return (
    <Box data-testid="upload-area">
      <Box
        role="button"
        sx={{
          display: 'flex',
          padding: variant === 'compact' ? sp[2].value : sp[3].value,
          border: `1px dashed ${color.type.muted.value}`,
          borderRadius: sp[1].value,
          cursor: 'pointer',
          alignItems: 'center',
          justifyContent: 'center',
        }}
        data-testid="upload-area-label"
        tabIndex={0}
        onClick={openFilePicker}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            openFilePicker();
          }
        }}
        onDragOver={(e) => e.preventDefault()}
        onDrop={async (e) => {
          e.preventDefault();
          const droppedFiles = Array.from(e.dataTransfer.files);
          await onFilesSelected(droppedFiles);
        }}
      >
        {isUploading ? (
          <Spinner variant="secondary" size="small" />
        ) : (
          <Box
            sx={{
              display: 'flex',
              flexDirection: variant === 'compact' ? 'row' : 'column',
              alignItems: 'center',
              gap: variant === 'compact' ? sp[1].value : sp[3].value,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: variant === 'compact' ? 'row' : 'column',
                alignItems: 'center',
                gap: variant === 'compact' ? sp[1].value : sp[0].value,
              }}
            >
              <UploadIcon />
              <Typography
                variant={variant === 'compact' ? 'body2' : 'h5'}
                color={color.type.default.value}
              >
                Drag files here or
              </Typography>
              <Typography
                variant={variant === 'compact' ? 'body2' : 'h5'}
                color={color.type.default.value}
              >
                <strong>select files to upload</strong>
              </Typography>
            </Box>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: sp[0].value,
                alignItems: 'center',
              }}
            >
              {supportedFormats && (
                <Typography variant="body2" color={color.type.muted.value}>
                  Formats: {supportedFormats.join(', ')}
                </Typography>
              )}
              <Typography variant="body2" color={color.type.muted.value}>
                {maxFileSizeLabel}
              </Typography>
            </Box>
          </Box>
        )}
        <input
          id={inputId}
          ref={fileInputRef}
          type="file"
          multiple
          accept={acceptedMimeTypes.join(',')}
          style={{ display: 'none' }}
          onChange={async (e) => {
            const selectedFiles = Array.from(e.target.files || []);
            await onFilesSelected(selectedFiles);
            e.target.value = '';
          }}
        />
      </Box>
    </Box>
  );
};
